// src/components/layout/AppLayout.tsx - Fixed all issues

import { Outlet, NavLink as RouterNavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { supabase } from '@/lib/supabaseClient';
import { getEmployeeProfile } from '@/lib/employeeStorage';
import {
  LayoutDashboard, FileText, Plus, Bell, LogOut, Menu, User,
  ChevronRight, Eye, BarChart3, UserCheck, CalendarCheck, Search,
  FileSignature, GraduationCap, DollarSign, TrendingUp, Lock,
  CheckCheck, Info, AlertTriangle, CheckCircle, XCircle, Shield,
  Settings, Database, Inbox, ClipboardList, Home, BarChart, Download, X, Users,
  Briefcase, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useState, useEffect, useRef } from 'react';
import { ROLE_LABELS, STAGES, Stage } from '@/types';
import { ScrollArea } from '@/components/ui/scroll-area';

// ─── Design tokens + global styles ───────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

  :root {
    --c:       #FAF8F4;   /* cream bg         */
    --c2:      #F2EDE3;   /* cream hover      */
    --c3:      #E6DFD1;   /* border / divider */
    --forest:  #1C3830;   /* primary dark     */
    --fmid:    #2B5244;   /* forest mid       */
    --gold:    #C09A40;   /* accent           */
    --gold-bg: #F5EECB;   /* gold tint        */
    --stone:   #796F61;   /* muted text       */
    --ink:     #18150F;   /* body text        */
    --white:   #FFFFFF;
    --red:     #B83232;
    --sw:      258px;     /* sidebar width    */
    --hh:      60px;      /* header height    */
    --r:       9px;       /* border radius    */
  }

  .vv * { box-sizing: border-box; }
  .vv, .vv body { font-family: 'DM Sans', sans-serif; color: var(--ink); }

  .vv-scroll::-webkit-scrollbar { width: 3px; }
  .vv-scroll::-webkit-scrollbar-thumb { background: var(--c3); border-radius: 99px; }

  .vv-sidebar {
    width: var(--sw); flex-shrink: 0;
    background: var(--c);
    border-right: 1px solid var(--c3);
    display: flex; flex-direction: column;
  }
  @media (max-width: 767px) { .vv-sidebar { display: none !important; } }

  .vv-header {
    height: var(--hh); flex-shrink: 0;
    background: rgba(250,248,244,0.93);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--c3);
    display: flex; align-items: center;
    justify-content: space-between;
    padding: 0 22px; position: relative; z-index: 20;
  }

  .vv-link {
    display: flex; align-items: center; gap: 9px;
    padding: 7.5px 11px; border-radius: var(--r);
    font-size: 13.5px; font-weight: 450;
    color: var(--stone); text-decoration: none;
    transition: color .13s, background .13s;
    position: relative; letter-spacing: -.01em;
    white-space: nowrap;
  }
  .vv-link:hover { color: var(--forest); background: var(--c2); }
  .vv-link.on    { color: var(--forest); background: var(--c2); font-weight: 560; }
  .vv-link.on::after {
    content: ''; position: absolute;
    left: 0; top: 20%; height: 60%; width: 3px;
    background: var(--gold); border-radius: 0 3px 3px 0;
  }
  .vv-link.off { color: var(--c3); pointer-events: none; }

  .vv-sec {
    display: flex; align-items: center; gap: 7px;
    margin: 14px 11px 7px; font-size: 10px;
    font-weight: 650; letter-spacing: .09em;
    text-transform: uppercase; color: var(--stone); opacity: .45;
  }
  .vv-sec-line { flex: 1; height: 1px; background: var(--c3); }

  .vv-pill {
    margin-left: auto;
    background: var(--gold-bg); color: var(--gold);
    font-size: 10px; font-weight: 700;
    border-radius: 99px; min-width: 18px; height: 18px;
    display: flex; align-items: center; justify-content: center;
    padding: 0 5px;
  }

  .vv-ibtn {
    width: 34px; height: 34px; border-radius: 8px;
    border: 1px solid var(--c3); background: var(--white);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all .13s;
    color: var(--stone); position: relative; flex-shrink: 0;
  }
  .vv-ibtn:hover { background: var(--c2); color: var(--forest); border-color: var(--c3); }

  .vv-badge {
    position: absolute; top: -4px; right: -4px;
    background: var(--gold); color: var(--white);
    font-size: 9px; font-weight: 800; border-radius: 99px;
    min-width: 15px; height: 15px;
    display: flex; align-items: center; justify-content: center;
    padding: 0 3px; border: 1.5px solid var(--c);
  }

  .vv-av {
    border-radius: 50%;
    background: var(--gold-bg); color: var(--gold);
    font-weight: 650; font-size: 12px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; border: 1.5px solid var(--c3);
    overflow: hidden; cursor: pointer;
    transition: border-color .13s;
  }
  .vv-av:hover { border-color: var(--gold); }

  .vv-mark {
    width: 30px; height: 30px; border-radius: 8px;
    background: var(--forest); flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }

  .vv-bc {
    background: none; border: none; padding: 0;
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    color: var(--stone); cursor: pointer; transition: color .13s;
  }
  .vv-bc:hover { color: var(--forest); }

  .vv-nrow {
    display: flex; gap: 10px; padding: 11px 15px;
    border-bottom: 1px solid var(--c3);
    cursor: pointer; transition: background .12s;
  }
  .vv-nrow:hover { background: var(--c2); }
  .vv-nrow:last-child { border-bottom: none; }

  .vv-dd {
    background: var(--white) !important;
    border: 1px solid var(--c3) !important;
    border-radius: 13px !important;
    box-shadow: 0 10px 40px rgba(28,56,48,.10), 0 2px 8px rgba(28,56,48,.06) !important;
    padding: 6px !important;
  }
  .vv-di {
    border-radius: 7px !important; font-size: 13.5px !important;
    color: var(--ink) !important; padding: 8px 10px !important;
    gap: 9px !important; cursor: pointer !important;
    transition: background .12s !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .vv-di:hover, .vv-di:focus { background: var(--c2) !important; color: var(--forest) !important; }
  .vv-di.red { color: var(--red) !important; }
  .vv-di.red:hover { background: #FEF0EE !important; color: var(--red) !important; }

  .vv-hamburger { display: flex; }
  @media (min-width: 768px) { .vv-hamburger { display: none !important; } }

  .vv-ucard {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: 9px; cursor: default;
    transition: background .13s;
  }
  .vv-ucard:hover { background: var(--c2); }

  .vv-install {
    display: flex; align-items: center; gap: 5px;
    padding: 5px 11px; border-radius: 7px;
    border: 1px solid var(--c3); background: var(--white);
    font-size: 12px; font-weight: 500; color: var(--forest);
    cursor: pointer; transition: all .13s;
    font-family: 'DM Sans', sans-serif;
  }
  .vv-install:hover { background: var(--c2); }

  .vv-pname {
    font-family: 'Lora', serif; font-style: italic;
    font-size: 12px; color: var(--gold);
    padding: 2px 11px 6px; display: block;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
`;

function VVStyles() {
  useEffect(() => {
    const id = 'vv-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = STYLES;
      document.head.prepend(el);
    }
  }, []);
  return null;
}

function Mark() {
  return (
    <div className="vv-mark">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#C09A40" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 1.5 L14.5 8 L8 14.5 L1.5 8 Z" />
        <path d="M8 5 L11 8 L8 11 L5 8 Z" strokeWidth="1.4" />
      </svg>
    </div>
  );
}

function InstallButton() {
  const [prompt, setPrompt] = useState<any>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const h = (e: any) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', h);
    window.addEventListener('appinstalled', () => { setInstalled(true); setPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  if (!prompt || installed) return null;

  return (
    <button className="vv-install" onClick={async () => {
      if (!prompt) return;
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') setPrompt(null);
    }}>
      <Download size={12} />
      Install
    </button>
  );
}

function NIcon({ type }: { type?: string }) {
  const s = { flexShrink: 0 };
  if (type === 'success') return <CheckCircle size={14} style={{ ...s, color: '#2E7D52' }} />;
  if (type === 'warning') return <AlertTriangle size={14} style={{ ...s, color: '#C09A40' }} />;
  if (type === 'error')   return <XCircle size={14} style={{ ...s, color: '#B83232' }} />;
  return <Info size={14} style={{ ...s, color: '#4F82B2' }} />;
}

type TabKey = 'overview'|'analysis'|'assignment'|'meeting'|'contract'|'bootcamp'|'diligence'|'funding'|'projections';
const PITCH_TABS: { key: TabKey; label: string; icon: React.ElementType; stage: Stage }[] = [
  { key:'overview',    label:'Pitch Overview', icon:Eye,          stage:'idea'               },
  { key:'analysis',   label:'Analysis',        icon:BarChart3,    stage:'analysis'           },
  { key:'assignment', label:'Assignment',       icon:UserCheck,    stage:'awaiting_assignment'},
  { key:'meeting',    label:'Meeting',          icon:CalendarCheck,stage:'validation'         },
  { key:'contract',   label:'Contract',         icon:FileSignature,stage:'contract'           },
  { key:'bootcamp',   label:'Bootcamp',         icon:GraduationCap,stage:'bootcamp'           },
  { key:'diligence',  label:'Due Diligence',    icon:Search,       stage:'diligence'          },
  { key:'funding',    label:'Funding',          icon:DollarSign,   stage:'funding'            },
  { key:'projections',label:'Projections',      icon:TrendingUp,   stage:'analysis'           },
];

function Sec({ label }: { label: string }) {
  return (
    <div className="vv-sec">
      <div className="vv-sec-line" />
      <span>{label}</span>
      <div className="vv-sec-line" />
    </div>
  );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth();
  const { pitches, getNotificationsForUser } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState(0);
  const [pendingInterests, setPendingInterests] = useState(0);
  const [deletionRequestsCount, setDeletionRequestsCount] = useState(0);
  const [passedPitchesCount, setPassedPitchesCount] = useState(0);

  const unread = user ? getNotificationsForUser(user.id).filter(n => !n.read).length : 0;
  const isBA   = user?.analyst_type === 'business';
  const isPBA  = user?.analyst_type === 'problem_bank';
  const isSA   = user?.role === 'senior_analyst';
  const isAdmin = user?.role === 'admin';
  const isF    = user?.role === 'founder';
  
  // Dashboard destination - admin does NOT get a dashboard button (handled separately)
  const dash = isSA ? '/senior-analyst' : (user?.role === 'analyst' ? '/analyst-dashboard' : (user?.role === 'founder' ? '/dashboard' : ''));

  useEffect(() => {
    if (user) {
      supabase.from('external_submissions')
        .select('*', { count: 'exact', head: true })
        .in('status', ['pending','under_review'])
        .then(({ count }) => { if (count != null) setPending(count); })
        .catch(err => console.warn('Failed to fetch submissions:', err));
    }
  }, [user]);

  useEffect(() => {
    if (user && isBA) {
      supabase.from('squad_problem_interests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending')
        .then(({ count }) => { if (count != null) setPendingInterests(count); })
        .catch(err => console.warn('Failed to fetch squad interests:', err));
    }
  }, [user, isBA]);

  useEffect(() => {
    if (user && isSA) {
      supabase.from('problem_deletion_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending')
        .then(({ count }) => { if (count != null) setDeletionRequestsCount(count); })
        .catch(err => console.warn('Failed to fetch deletion requests:', err));
    }
  }, [user, isSA]);

  useEffect(() => {
    if (user && isSA) {
      const passed = pitches.filter(p => 
        !p.deletedAt && 
        p.analysisScores && 
        p.analysisScores.overall >= 70 && 
        p.stage === 'awaiting_assignment'
      ).length;
      setPassedPitchesCount(passed);
    }
  }, [user, isSA, pitches]);

  const pitchMatch = location.pathname.match(/^\/pitch\/(.+)$/);
  const pitchId    = pitchMatch?.[1] ?? null;
  const pitch      = pitchId ? pitches.find(p => p.id === pitchId) : null;
  const curTab     = searchParams.get('tab') || 'overview';
  const getStageIdx = (s: Stage) => STAGES.indexOf(s);
  const curStageIdx = pitch ? getStageIdx(pitch.stage) : -1;

  const cls = (path: string) => {
    const active = location.pathname === path ||
      (path !== '/dashboard' && path !== '/pitch/new/basics' &&
        location.pathname.startsWith(path) && !pitchMatch);
    return `vv-link${active ? ' on' : ''}`;
  };

  return (
    <nav style={{ display:'flex', flexDirection:'column', gap:2, padding:'10px 9px' }}>
      {/* Dashboard button - only for non-admin users */}
      {(isSA || isF || isBA || isPBA) && dash && (
        <RouterNavLink to={dash} className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
          <LayoutDashboard size={15} strokeWidth={1.7} /> Dashboard
        </RouterNavLink>
      )}

      {/* ADMIN DASHBOARD SECTION - Only for admin users */}
      {isAdmin && (
        <>
          <Sec label="Admin Portal" />
          <RouterNavLink 
            to="/admin-portal"
            className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`}
            onClick={onNavigate}
          >
            <Shield size={15} strokeWidth={1.7} /> Admin Dashboard
          </RouterNavLink>
          <RouterNavLink 
            to="/admin-portal/users"
            className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`}
            onClick={onNavigate}
          >
            <Users size={15} strokeWidth={1.7} /> User Management
          </RouterNavLink>
          <RouterNavLink 
            to="/admin-portal/system"
            className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`}
            onClick={onNavigate}
          >
            <Settings size={15} strokeWidth={1.7} /> System Settings
          </RouterNavLink>
          <RouterNavLink 
            to="/admin-portal/audit"
            className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`}
            onClick={onNavigate}
          >
            <FileText size={15} strokeWidth={1.7} /> Audit Logs
          </RouterNavLink>
        </>
      )}

      {/* SENIOR ANALYST SECTION */}
      {isSA && (
        <>
          <Sec label="Senior Analyst" />
          <RouterNavLink 
            to="/senior-analyst?tab=problems" 
            className={() => {
              const isActiveTab = (location.pathname === '/senior-analyst' && !searchParams.get('tab')) || searchParams.get('tab') === 'problems';
              return `vv-link${isActiveTab ? ' on' : ''}`;
            }}
            onClick={onNavigate}
          >
            <Database size={15} strokeWidth={1.7} /> Problem Bank
          </RouterNavLink>
          <RouterNavLink 
            to="/senior-analyst/pitches"
            className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`}
            onClick={onNavigate}
          >
            <Briefcase size={15} strokeWidth={1.7} /> Founder Pitches
            {passedPitchesCount > 0 && <span className="vv-pill">{passedPitchesCount}</span>}
          </RouterNavLink>
          <RouterNavLink 
            to="/senior-analyst?tab=deletion-requests" 
            className={() => {
              const isActiveTab = searchParams.get('tab') === 'deletion-requests';
              return `vv-link${isActiveTab ? ' on' : ''}`;
            }}
            onClick={onNavigate}
          >
            <Trash2 size={15} strokeWidth={1.7} /> Deletion Requests
            {deletionRequestsCount > 0 && <span className="vv-pill" style={{ background: 'var(--red)', color: 'white' }}>{deletionRequestsCount}</span>}
          </RouterNavLink>
        </>
      )}

      {isF && (
        <RouterNavLink to="/pitch/new/basics" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
          <Plus size={15} strokeWidth={1.7} /> New Pitch
        </RouterNavLink>
      )}

      {isF && (
        <RouterNavLink to="/pitches" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
          <FileText size={15} strokeWidth={1.7} /> Pitches
        </RouterNavLink>
      )}

      {isBA && (
        <RouterNavLink to="/analyst/pitches" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
          <FileText size={15} strokeWidth={1.7} /> Pitches
        </RouterNavLink>
      )}

      <RouterNavLink to="/notifications" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
        <Bell size={15} strokeWidth={1.7} /> Notifications
        {unread > 0 && <span className="vv-pill">{unread}</span>}
      </RouterNavLink>

      {isSA && (
        <>
          <Sec label="Problem Bank Tools" />
          <RouterNavLink to="/problem-bank" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <Database size={15} strokeWidth={1.7} /> Browse Problems
          </RouterNavLink>
          <RouterNavLink to="/problem-bank/submissions" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <Inbox size={15} strokeWidth={1.7} /> External Submissions
            {pending > 0 && <span className="vv-pill">{pending}</span>}
          </RouterNavLink>
          <RouterNavLink to="/problem-bank/my-assignments" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <ClipboardList size={15} strokeWidth={1.7} /> My Assignments
          </RouterNavLink>
        </>
      )}

      {isPBA && (
        <>
          <Sec label="Problem Bank" />
          <RouterNavLink to="/problem-bank" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <Database size={15} strokeWidth={1.7} /> Browse Problems
          </RouterNavLink>
          <RouterNavLink to="/problem-bank/submissions" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <Inbox size={15} strokeWidth={1.7} /> External Submissions
            {pending > 0 && <span className="vv-pill">{pending}</span>}
          </RouterNavLink>
          <RouterNavLink to="/problem-bank/my-assignments" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <ClipboardList size={15} strokeWidth={1.7} /> My Assignments
          </RouterNavLink>
        </>
      )}

      {isBA && (
        <>
          <Sec label="Squad Management" />
          <RouterNavLink to="/problem-bank" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <Database size={15} strokeWidth={1.7} /> Problem Bank
          </RouterNavLink>
          <RouterNavLink to="/analyst/squad-interests" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <Users size={15} strokeWidth={1.7} /> Squad Interests
            {pendingInterests > 0 && <span className="vv-pill">{pendingInterests}</span>}
          </RouterNavLink>
          <RouterNavLink to="/problem-bank/my-assignments" className={({ isActive }) => `vv-link${isActive ? ' on' : ''}`} onClick={onNavigate}>
            <ClipboardList size={15} strokeWidth={1.7} /> Squad Assignments
          </RouterNavLink>
        </>
      )}

      {pitch && (
        <>
          <div style={{ margin:'12px 0 2px' }}>
            <span className="vv-pname">{pitch.companyName}</span>
          </div>
          {PITCH_TABS.map(tab => {
            const accessible = curStageIdx >= getStageIdx(tab.stage);
            const isOn       = curTab === tab.key;
            const Icon       = tab.icon;
            return (
              <RouterNavLink
                key={tab.key}
                to={`/pitch/${pitchId}?tab=${tab.key}`}
                onClick={e => { if (!accessible) { e.preventDefault(); return; } onNavigate?.(); }}
                className={() => `vv-link${!accessible ? ' off' : isOn ? ' on' : ''}`}
              >
                {accessible
                  ? <Icon size={15} strokeWidth={1.7} />
                  : <Lock size={13} strokeWidth={1.7} style={{ opacity:.35 }} />}
                {tab.label}
              </RouterNavLink>
            );
          })}
        </>
      )}
    </nav>
  );
}

function Breadcrumbs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const parts = pathname.split('/').filter(Boolean);
  const dash  = user?.role === 'admin' ? '/admin-portal' : (user?.role === 'analyst' || user?.role === 'senior_analyst' ? '/analyst-dashboard' : '/dashboard');
  const crumbs: { label:string; path:string; cur?:boolean }[] = [{ label:'Dashboard', path:dash }];

  const searchParams = new URLSearchParams(search);
  const seniorTab = searchParams.get('tab');

  if (parts[0] === 'admin-portal') {
    crumbs.push({ label:'Admin Portal', path:'/admin-portal' });
    if (parts[1] === 'users') crumbs.push({ label:'User Management', path:'#', cur:true });
    else if (parts[1] === 'system') crumbs.push({ label:'System Settings', path:'#', cur:true });
    else if (parts[1] === 'audit') crumbs.push({ label:'Audit Logs', path:'#', cur:true });
  } else if (parts[0] === 'senior-analyst') {
    if (parts[1] === 'pitches') {
      crumbs.push({ label:'Senior Analyst', path:'/senior-analyst' });
      crumbs.push({ label:'Founder Pitches', path:'#', cur:true });
    } else {
      crumbs.push({ label:'Senior Analyst', path:'/senior-analyst' });
      if (seniorTab === 'deletion-requests') crumbs.push({ label:'Deletion Requests', path:'#', cur:true });
      else crumbs.push({ label:'Problem Bank', path:'#', cur:true });
    }
  } else if (parts[0] === 'pitches') {
    crumbs.push({ label:'Pitches', path:'/pitches' });
    if (parts[1] === 'new') crumbs.push({ label:'New Pitch', path:'#', cur:true });
    else if (parts[1])       crumbs.push({ label:'Pitch Details', path:'#', cur:true });
  } else if (parts[0] === 'pitch') {
    crumbs.push({ label:'Pitches', path:'/pitches' });
    if (parts[1] === 'new') crumbs.push({ label:'New Pitch', path:'#', cur:true });
    else if (parts[1])       crumbs.push({ label:'Pitch Details', path:'#', cur:true });
  } else if (parts[0] === 'problem-bank') {
    crumbs.push({ label:'Problem Bank', path:'/problem-bank' });
    if (parts[1] === 'submissions') {
      crumbs.push({ label:'External Submissions', path:'/problem-bank/submissions' });
      if (parts[2] === 'review' && parts[3]) crumbs.push({ label:'Review Submission', path:'#', cur:true });
    } else if (parts[1] === 'my-assignments') {
      crumbs.push({ label:'My Assignments', path:'/problem-bank/my-assignments' });
    } else if (parts[1] === 'new') {
      crumbs.push({ label:'New Problem', path:'#', cur:true });
    } else if (parts[1] === 'problem' && parts[2]) {
      crumbs.push({ label:'Problem Details', path:`/problem-bank/problem/${parts[2]}` });
      if (parts[3] === 'squads') crumbs.push({ label:'Assign to Squad', path:'#', cur:true });
      else if (parts[3] === 'progress') crumbs.push({ label:'Progress Tracker', path:'#', cur:true });
    }
  } else if (parts[0] === 'analyst') {
    crumbs.push({ label:'Analyst', path:'/analyst-dashboard' });
    if (parts[1] === 'squad-interests') crumbs.push({ label:'Squad Interests', path:'#', cur:true });
  } else if (parts[0] === 'forge') {
    crumbs.push({ label:'Forge', path:'/forge' });
    if (parts[1] === 'dashboard') crumbs.push({ label:'Dashboard', path:'#', cur:true });
    else if (parts[1] === 'problem-bank') {
      crumbs.push({ label:'Problem Bank', path:'/forge/problem-bank' });
      if (parts[2] === 'problem' && parts[3]) crumbs.push({ label:'Problem Details', path:'#', cur:true });
    } else if (parts[1] === 'squad' && parts[2]) crumbs.push({ label:'Squad Profile', path:'#', cur:true });
    else if (parts[1] === 'auth') crumbs.push({ label:'Sign In', path:'#', cur:true });
    else if (parts[1] === 'register') crumbs.push({ label:'Register', path:'#', cur:true });
  } else if (parts[0] === 'founder-profile') {
    crumbs.push({ label:'Profile Settings', path:'#', cur:true });
  } else if (parts[0] === 'profile') {
    crumbs.push({ label:'Profile Settings', path:'#', cur:true });
  } else if (parts[0] === 'notifications') {
    crumbs.push({ label:'Notifications', path:'/notifications' });
  }

  return (
    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
      <Home size={13} style={{ color:'var(--stone)', opacity:.45, flexShrink:0, marginRight:2 }} />
      {crumbs.map((c, i) => (
        <span key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
          {i > 0 && <ChevronRight size={11} style={{ color:'var(--stone)', opacity:.35, flexShrink:0 }} />}
          {!c.cur
            ? <button className="vv-bc" onClick={() => navigate(c.path)}>{c.label}</button>
            : <span style={{ fontSize:13, fontWeight:560, color:'var(--forest)' }}>{c.label}</span>
          }
        </span>
      ))}
    </div>
  );
}

export default function AppLayout() {
  const { user, isLoading, logout } = useAuth();
  const { getNotificationsForUser, markNotificationRead, markAllNotificationsRead } = useData();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.error('AppLayout rendered without user!');
    return null;
  }

  const notifs     = user ? getNotificationsForUser(user.id) : [];
  const unread     = notifs.filter(n => !n.read).length;

  const onNotif = (n: any) => {
    markNotificationRead(n.id);
    navigate(n.tab ? `/pitch/${n.pitchId}?tab=${n.tab}` : `/pitch/${n.pitchId}`);
    setMobileOpen(false);
  };

  const timeAgo = (d: string) => {
    const ms = Date.now() - +new Date(d);
    const m = Math.floor(ms/60000), h = Math.floor(ms/3600000), dy = Math.floor(ms/86400000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (dy < 7) return `${dy}d ago`;
    return new Date(d).toLocaleDateString();
  };

  const initials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2);

  const getEmployeeProfileSafe = (email: string, role: string) => {
    try {
      return getEmployeeProfile(email, role);
    } catch (error) {
      console.warn('Failed to get employee profile:', error);
      return { imageUrl: null };
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <VVStyles />
      <div className="vv" style={{ display:'flex', height:'100vh', width:'100%', overflow:'hidden', background:'var(--c)' }}>

        <aside className="vv-sidebar">
          <div style={{ height:'var(--hh)', display:'flex', alignItems:'center', gap:11, padding:'0 17px', borderBottom:'1px solid var(--c3)', flexShrink:0 }}>
            <Mark />
            <div>
              <div style={{ fontFamily:"'Lora', serif", fontWeight:600, fontSize:14.5, color:'var(--forest)', letterSpacing:'-.02em', lineHeight:1.2 }}>
                Vett &amp; Venture
              </div>
              <div style={{ fontSize:10.5, color:'var(--stone)', letterSpacing:'.04em', opacity:.7 }}>
                VENTURE PLATFORM
              </div>
            </div>
          </div>

          <div className="vv-scroll" style={{ flex:1, overflowY:'auto' }}>
            <SidebarNav />
          </div>

          {user && (
            <div style={{ borderTop:'1px solid var(--c3)', padding:'11px 9px', flexShrink:0 }}>
              <div className="vv-ucard">
                <div className="vv-av" style={{ width:34, height:34, fontSize:12 }}>
                  {(() => {
                    const p = getEmployeeProfileSafe(user.email, user.role);
                    if (p.imageUrl) return <img src={p.imageUrl} alt={user.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />;
                    return initials(user.name);
                  })()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:560, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.3 }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--stone)', marginTop:1 }}>
                    {ROLE_LABELS[user.role]}
                  </div>
                </div>
              </div>
            </div>
          )}
        </aside>

        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

          <header className="vv-header">
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="vv-ibtn vv-hamburger">
                    <Menu size={16} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" style={{ width:258, padding:0, background:'var(--c)', border:'none', borderRight:'1px solid var(--c3)' }} className="[&>button]:hidden">
                  <div style={{ height:60, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 17px', borderBottom:'1px solid var(--c3)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <Mark />
                      <span style={{ fontFamily:"'Lora', serif", fontWeight:600, fontSize:14, color:'var(--forest)' }}>
                        Vett &amp; Venture
                      </span>
                    </div>
                    <SheetClose asChild>
                      <button className="vv-ibtn"><X size={15} /></button>
                    </SheetClose>
                  </div>
                  <div className="vv-scroll" style={{ overflowY:'auto', height:'calc(100% - 60px)' }}>
                    <SidebarNav onNavigate={() => setMobileOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>

              <Breadcrumbs />
            </div>

            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <InstallButton />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="vv-ibtn">
                    <Bell size={16} />
                    {unread > 0 && <span className="vv-badge">{unread > 9 ? '9+' : unread}</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" style={{ width:380, maxHeight:560, padding:0, background:'var(--white)', border:'1px solid var(--c3)', borderRadius:14, boxShadow:'0 12px 44px rgba(28,56,48,.12)', overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px 12px', borderBottom:'1px solid var(--c3)' }}>
                    <span style={{ fontFamily:"'Lora', serif", fontWeight:600, fontSize:15, color:'var(--forest)' }}>Notifications</span>
                    {unread > 0 && (
                      <button onClick={() => user && markAllNotificationsRead(user.id)}
                        style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'var(--gold)', fontWeight:500, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                        <CheckCheck size={12} /> Mark all read
                      </button>
                    )}
                  </div>
                  <ScrollArea style={{ height:380 }}>
                    {notifs.length === 0
                      ? (
                        <div style={{ padding:'44px 16px', textAlign:'center' }}>
                          <Bell size={30} style={{ color:'var(--c3)', margin:'0 auto 10px', display:'block' }} />
                          <p style={{ fontSize:13, color:'var(--stone)', margin:0 }}>No notifications yet</p>
                        </div>
                      )
                      : notifs.map(n => (
                        <div key={n.id} className="vv-nrow" onClick={() => onNotif(n)}
                          style={{ background: !n.read ? 'rgba(192,154,64,.04)' : 'transparent' }}>
                          <div style={{ marginTop:2, flexShrink:0 }}><NIcon type={n.type} /></div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ margin:'0 0 3px', fontSize:13, color:'var(--ink)', fontWeight:!n.read?560:400, lineHeight:1.4 }}>
                              {n.message}
                            </p>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ fontSize:11.5, color:'var(--stone)' }}>{timeAgo(n.createdAt)}</span>
                              {n.tab && <>
                                <span style={{ color:'var(--c3)' }}>·</span>
                                <span style={{ fontSize:10.5, color:'var(--gold)', background:'var(--gold-bg)', borderRadius:4, padding:'1px 6px', fontWeight:500, textTransform:'capitalize' }}>
                                  {n.tab}
                                </span>
                              </>}
                            </div>
                          </div>
                          {!n.read && <div style={{ width:6, height:6, background:'var(--gold)', borderRadius:'50%', flexShrink:0, marginTop:5 }} />}
                        </div>
                      ))
                    }
                  </ScrollArea>
                  {notifs.length > 0 && (
                    <div onClick={() => navigate('/notifications')}
                      style={{ textAlign:'center', padding:'10px', fontSize:12.5, color:'var(--gold)', cursor:'pointer', fontWeight:500, borderTop:'1px solid var(--c3)', transition:'background .12s' }}
                      onMouseEnter={e => (e.currentTarget.style.background='var(--c2)')}
                      onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                      View all notifications →
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="vv-av" style={{ width:34, height:34, fontSize:13 }}>
                    {user ? initials(user.name) : <User size={14} />}
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" style={{ width:220, padding:6, background:'var(--white)', border:'1px solid var(--c3)', borderRadius:13, boxShadow:'0 10px 40px rgba(28,56,48,.10)' }}>
                  {user && (
                    <div style={{ padding:'10px 12px 13px', borderBottom:'1px solid var(--c3)', marginBottom:6 }}>
                      <div style={{ fontFamily:"'Lora', serif", fontWeight:600, fontSize:14, color:'var(--forest)', lineHeight:1.3 }}>{user.name}</div>
                      <div style={{ fontSize:11.5, color:'var(--stone)', marginTop:2 }}>{user.email}</div>
                      <span style={{ display:'inline-block', marginTop:7, fontSize:10.5, fontWeight:650, color:'var(--gold)', background:'var(--gold-bg)', borderRadius:5, padding:'2px 7px', letterSpacing:'.02em' }}>
                        {ROLE_LABELS[user.role]}
                      </span>
                    </div>
                  )}
                  
                  <DropdownMenuItem 
                    className="vv-di" 
                    onClick={() => {
                      if (user?.role === 'founder') {
                        navigate('/founder-profile');
                      } else {
                        navigate('/profile');
                      }
                    }}
                  >
                    <Settings size={14} strokeWidth={1.7} style={{ color:'var(--stone)' }} /> Profile Settings
                  </DropdownMenuItem>
                  
                  {(user?.role === 'senior_analyst' || user?.role === 'analyst') && (
                    <DropdownMenuItem className="vv-di" onClick={() => navigate('/problem-bank')}>
                      <Database size={14} strokeWidth={1.7} style={{ color:'var(--stone)' }} /> Problem Bank
                    </DropdownMenuItem>
                  )}
                  
                  <div style={{ height:1, background:'var(--c3)', margin:'6px 4px' }} />
                  <DropdownMenuItem className="vv-di red" onClick={handleLogout}>
                    <LogOut size={14} strokeWidth={1.7} /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main style={{ flex:1, overflowY:'auto', padding:'clamp(18px, 3vw, 42px)', background:'var(--c)' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}